import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Link from '@mui/joy/Link'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import SearchIcon from '@mui/icons-material/Search'
import { CircularProgress } from '@mui/joy'
import { ELDIntegrationRequestStatus } from '@numeo/types-main-mobile'
import axios from 'axios'
import { formatToUserLocalTime } from '@/utils/date-formatting'

interface EldIntegrationRequestRow {
    id: string
    projectName: string
    requestedBy: string
    providerName: string
    websiteUrl: string
    notes: string
    status: ELDIntegrationRequestStatus
    createdAt: string | null
}

const PAGE_SIZE = 50

const STATUS_COLORS: Record<ELDIntegrationRequestStatus, 'warning' | 'primary' | 'success' | 'danger'> = {
    [ELDIntegrationRequestStatus.Pending]: 'warning',
    [ELDIntegrationRequestStatus.Reviewed]: 'primary',
    [ELDIntegrationRequestStatus.Completed]: 'success',
    [ELDIntegrationRequestStatus.Declined]: 'danger',
}

const formatStatusLabel = (status: string): string => status.charAt(0).toUpperCase() + status.slice(1)

export default function EldIntegrationRequests() {
    const [rows, setRows] = React.useState<EldIntegrationRequestRow[]>([])
    const [page, setPage] = React.useState(1)
    const [loading, setLoading] = React.useState(true)
    const [fetching, setFetching] = React.useState(false)
    const [totalCount, setTotalCount] = React.useState(0)
    const [totalPages, setTotalPages] = React.useState(0)

    const [projectFilter, setProjectFilter] = React.useState('')
    const [providerFilter, setProviderFilter] = React.useState('')
    const [appliedProjectFilter, setAppliedProjectFilter] = React.useState('')
    const [appliedProviderFilter, setAppliedProviderFilter] = React.useState('')
    const [statusFilter, setStatusFilter] = React.useState<ELDIntegrationRequestStatus | ''>('')
    const [updatingId, setUpdatingId] = React.useState<string | null>(null)

    const handleStatusChange = async (id: string, status: ELDIntegrationRequestStatus) => {
        const previousRows = rows
        setUpdatingId(id)
        setRows((current) => current.map((row) => (row.id === id ? { ...row, status } : row)))
        try {
            await axios.patch(`gluon/eld-integration-requests/${id}/status`, { status })
        } catch (e) {
            console.error('Error updating ELD integration request status:', e)
            setRows(previousRows)
        } finally {
            setUpdatingId(null)
        }
    }

    const handleFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            setPage(1)
            setAppliedProjectFilter(projectFilter)
            setAppliedProviderFilter(providerFilter)
        }
    }

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const isInitialLoad = page === 1 && !appliedProjectFilter && !appliedProviderFilter && !statusFilter

                if (isInitialLoad) {
                    setLoading(true)
                } else {
                    setFetching(true)
                }

                const params = new URLSearchParams()
                params.append('page', page.toString())
                params.append('pageSize', PAGE_SIZE.toString())
                if (appliedProjectFilter) params.append('projectName', appliedProjectFilter)
                if (appliedProviderFilter) params.append('providerName', appliedProviderFilter)
                if (statusFilter) params.append('status', statusFilter)

                const res = await axios.get(`gluon/eld-integration-requests?${params.toString()}`)
                const data = res.data || {}
                setRows(Array.isArray(data.rows) ? data.rows : [])
                setTotalCount(typeof data.total === 'number' ? data.total : 0)
                setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0)
            } catch (e) {
                console.error('Error fetching ELD integration requests:', e)
                setRows([])
                setTotalCount(0)
                setTotalPages(0)
            } finally {
                setLoading(false)
                setFetching(false)
            }
        }

        fetchData()
    }, [page, appliedProjectFilter, appliedProviderFilter, statusFilter])

    React.useEffect(() => {
        if (page > totalPages && totalPages > 0) {
            setPage(1)
        }
    }, [totalPages, page])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        )
    }

    return (
        <React.Fragment>
            <Box
                sx={{
                    display: 'flex',
                    my: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography level="h2" component="h1">
                        ELD Integration Requests ({totalCount})
                    </Typography>
                    {fetching && <CircularProgress size="sm" />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="sm" variant="outlined" disabled={page === 1 || fetching} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page >= totalPages || fetching} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        width: '100%',
                        alignItems: 'flex-end',
                    }}
                >
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Project Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by project name..."
                            startDecorator={<SearchIcon />}
                            value={projectFilter}
                            onChange={(e) => setProjectFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Provider Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by provider name..."
                            startDecorator={<SearchIcon />}
                            value={providerFilter}
                            onChange={(e) => setProviderFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ minWidth: 180 }} size="sm">
                        <FormLabel>Status</FormLabel>
                        <Select<ELDIntegrationRequestStatus | ''>
                            size="sm"
                            value={statusFilter}
                            onChange={(_, value) => {
                                setPage(1)
                                setStatusFilter(value ?? '')
                            }}
                        >
                            <Option value="">All</Option>
                            {Object.values(ELDIntegrationRequestStatus).map((status) => (
                                <Option key={status} value={status}>
                                    {formatStatusLabel(status)}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Sheet
                variant="outlined"
                sx={{
                    display: { xs: 'none', sm: 'initial' },
                    width: '100%',
                    borderRadius: 'sm',
                    flexShrink: 1,
                    overflow: 'auto',
                    minHeight: 0,
                }}
            >
                <Table
                    aria-labelledby="eld-integration-requests-table-title"
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-paddingY': '4px',
                        '--TableCell-paddingX': '8px',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 160, padding: '12px 6px' }}>Provider</th>
                            <th style={{ width: 180, padding: '12px 6px' }}>Website</th>
                            <th style={{ width: 220, padding: '12px 6px' }}>Notes</th>
                            <th style={{ width: 140, padding: '12px 6px' }}>Project</th>
                            <th style={{ width: 200, padding: '12px 6px' }}>Requested By</th>
                            <th style={{ width: 150, padding: '12px 6px' }}>Status</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>Requested At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <Typography level="body-xs" fontWeight="lg">
                                        {row.providerName}
                                    </Typography>
                                </td>
                                <td>
                                    {row.websiteUrl ? (
                                        <Link level="body-xs" href={row.websiteUrl} target="_blank" rel="noopener noreferrer">
                                            {row.websiteUrl}
                                        </Link>
                                    ) : (
                                        <Typography level="body-xs">—</Typography>
                                    )}
                                </td>
                                <td>
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            maxWidth: 220,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            cursor: row.notes ? 'pointer' : 'default',
                                        }}
                                        title={row.notes}
                                    >
                                        {row.notes || '—'}
                                    </Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.projectName}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.requestedBy}</Typography>
                                </td>
                                <td>
                                    <Select<ELDIntegrationRequestStatus>
                                        size="sm"
                                        variant="soft"
                                        color={STATUS_COLORS[row.status] || 'neutral'}
                                        value={row.status}
                                        disabled={updatingId === row.id}
                                        onChange={(_, value) => {
                                            if (value && value !== row.status) handleStatusChange(row.id, value)
                                        }}
                                    >
                                        {Object.values(ELDIntegrationRequestStatus).map((status) => (
                                            <Option key={status} value={status}>
                                                {formatStatusLabel(status)}
                                            </Option>
                                        ))}
                                    </Select>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.createdAt ? formatToUserLocalTime(row.createdAt) : 'N/A'}</Typography>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={7} style={{ textAlign: 'center', padding: '16px' }}>
                                    <Typography level="body-sm">No data available</Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>
        </React.Fragment>
    )
}
